import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  ArrowLeft, RefreshCw, Clock, CheckCircle, XCircle, Loader2,
  ChevronDown, ChevronRight, Eye, X, Check, AlertTriangle, Wand2,
  GitBranch, FileText, Shield, Database, Route, BookOpen,
  SkipForward, Pause, Circle, MessageSquare, ArrowRight,
  Search, TestTube, Brain, Lock, ClipboardCheck, ShieldCheck,
} from 'lucide-react';
import api, { buildProtectedStreamUrl, type SyncRun, type SyncPhase, authHeaders, fetchStreamToken } from '../api';
import { openSyncRunStream } from '../lib/sync-stream';
import { openLiveSyncConversation } from '../lib/live-codex.js';
import { getCachedSession } from '../context/AuthContext';
import CodeHealth from '../components/CodeHealth';
import { ArchReviewModal } from '../components/ArchReviewModal';
import { ReviewComparison } from '../components/ReviewComparison';
import { SyncNextStepCard } from '../components/SyncNextStepCard';
import { syncRunMismatch } from '../lib/sync-route';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.ocnexus.dev';

// ─── Task 2: Human-Readable Phase Display Mapping ──────────────

const PHASE_DISPLAY: Record<string, { name: string; description: string; icon: React.ElementType }> = {
  repo_scan:           { name: 'Scanning your code',          description: 'Reading your repository files and structure',                    icon: Search },
  package_detection:   { name: 'Detecting tech stack',        description: 'Identifying languages, frameworks, and dependencies',            icon: Database },
  convention_analysis: { name: 'Learning your patterns',      description: 'Analyzing naming conventions, file structure, and import style', icon: FileText },
  doc_audit:           { name: 'Checking documentation',      description: 'Reviewing existing docs and identifying gaps',                  icon: BookOpen },
  agents_md:           { name: 'Writing coding rules',        description: 'Creating a conventions file so OCNexus matches your style',     icon: ClipboardCheck },
  worldview_generation:{ name: 'Building project overview',   description: 'Generating a comprehensive summary of your codebase',           icon: Eye },
  routing_rules:       { name: 'Mapping code paths',          description: 'Understanding how files relate to features and workflows',      icon: Route },
  test_audit:          { name: 'Analyzing test coverage',     description: 'Checking your test framework, test count, and CI pipeline',     icon: TestTube },
  knowledge_refresh:   { name: 'Compiling project knowledge', description: 'Building the context OCNexus uses to generate better code',     icon: Brain },
  guardian_baseline:   { name: 'Setting quality baseline',    description: 'Capturing current code quality scores to track improvements',   icon: Shield },
  security_scan:       { name: 'Security check',              description: 'Scanning for vulnerabilities and security patterns',             icon: Lock },
  quality_checks:      { name: 'Running quality checks',      description: 'Checking CI/CD setup, documentation quality, and best practices', icon: Wand2 },
  sast_scan:           { name: 'SAST security scan',          description: 'Running Semgrep static analysis for security vulnerabilities',    icon: ShieldCheck },
  manus_audit:         { name: 'Architectural review',        description: 'Deep analysis of architecture, security, tech debt, and risk areas', icon: Shield },
};

// AC-10: Fallback for unknown phases
function getPhaseDisplay(phaseId: string) {
  return PHASE_DISPLAY[phaseId] || { name: phaseId.replace(/_/g, ' '), description: '', icon: Circle };
}

// ─── Task 6: Time Estimation ───────────────────────────────────

const PHASE_ESTIMATES_MS: Record<string, number> = {
  repo_scan: 5_000,
  package_detection: 3_000,
  convention_analysis: 8_000,
  doc_audit: 15_000,
  agents_md: 20_000,
  worldview_generation: 25_000,
  routing_rules: 5_000,
  test_audit: 8_000,
  knowledge_refresh: 10_000,
  guardian_baseline: 10_000,
  security_scan: 30_000,
  quality_checks: 5_000,
  sast_scan: 60_000,
  manus_audit: 600_000,
};

function median(values: number[]): number | null {
  const sorted = values.filter(Number.isFinite).sort((a, b) => a - b);
  if (!sorted.length) return null;
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

export function estimateRemaining(phases: SyncPhase[], elapsedMs = 0, previousDurationsMs: number[] = []): number {
  const completed = phases.filter(p => p.status === 'completed' || p.status === 'skipped');
  const previous = median(previousDurationsMs);
  const planned = phases.reduce((sum, p) => sum + (PHASE_ESTIMATES_MS[p.id] || 10_000), 0);
  const expectedDone = completed.reduce((sum, p) => sum + (PHASE_ESTIMATES_MS[p.id] || 10_000), 0);
  const actualDone = completed.reduce((sum, p) => sum + (p.duration_ms || 0), 0);
  const thisRunTotal = actualDone > 0 && expectedDone > 0 ? planned * actualDone / expectedDone : planned;
  const total = previous ?? Math.max(thisRunTotal, elapsedMs);
  const remaining = Math.max(0, total - elapsedMs);
  return Math.round(Math.min(remaining, previous !== null && previous < 180_000 ? 300_000 : remaining));
}

export function formatTimeEstimate(ms: number, isReview: boolean): string {
  if (isReview) return 'Waiting for your approval';
  if (ms <= 0) return 'Finishing up...';
  if (ms < 90_000) return 'About a minute remaining';
  return `~${Math.ceil(ms / 60_000)} minutes remaining`;
}

// ─── Helpers ────────────────────────────────────────────────────

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
}

function formatDurationShort(ms: number): string {
  if (ms < 1000) return '<1s';
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60000) return 'just now';
  if (ms < 3600000) return `${Math.floor(ms / 60000)}m ago`;
  if (ms < 86400000) return `${Math.floor(ms / 3600000)}h ago`;
  return `${Math.floor(ms / 86400000)}d ago`;
}

function statusColor(s: string): string {
  if (s === 'completed') return 'var(--green)';
  if (s === 'running' || s === 'review_required' || s === 'audit_pending') return 'var(--orange)';
  if (s === 'failed') return 'var(--red)';
  if (s === 'cancelled') return 'var(--text-dim)';
  return 'var(--text-muted)';
}

function statusLabel(s: string): string {
  if (s === 'review_required') return 'Review Required';
  if (s === 'audit_pending') return 'Audit Approval';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ─── Task 1: Review Gate Modal ─────────────────────────────────

interface ReviewModalProps {
  runId: string;
  agentsMdDiff: unknown;
  routingRulesDiff: unknown;
  packagesDiff: unknown;
  onApproved: () => void;
  onSkipped: () => void;
}

const ReviewModal: React.FC<ReviewModalProps> = ({
  runId, agentsMdDiff, routingRulesDiff, packagesDiff, onApproved, onSkipped,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [expandedFile, setExpandedFile] = useState<string | null>(null);

  const agentsDiff = agentsMdDiff as { changes?: Array<{ type: string; description: string }>; total_changes?: number; content?: string } | null;
  const routingDiff = routingRulesDiff as { changes?: unknown[]; total_changes?: number; content?: string } | null;
  const pkgDiff = packagesDiff as { added?: string[]; removed?: string[]; unchanged?: string[] } | null;

  // AC-7: Build dynamic file list — only scaffolded/modified files
  const files: Array<{ name: string; description: string; hasChanges: boolean; diffKey: string }> = [];

  if (agentsDiff && (agentsDiff.total_changes ?? 0) > 0) {
    const changeCount = agentsDiff.total_changes ?? 0;
    const changeTypes = agentsDiff.changes?.map(c => c.description).slice(0, 3).join(', ') || '';
    files.push({
      name: 'AGENTS.md',
      description: changeTypes ? `Updated: ${changeTypes}` : `${changeCount} coding convention${changeCount > 1 ? 's' : ''} defined`,
      hasChanges: true,
      diffKey: 'agents',
    });
  }

  if (routingDiff && (routingDiff.total_changes ?? 0) > 0) {
    files.push({
      name: 'Routing Rules',
      description: `${routingDiff.total_changes} routing rule${(routingDiff.total_changes ?? 0) > 1 ? 's' : ''} updated`,
      hasChanges: true,
      diffKey: 'routing',
    });
  }

  if (pkgDiff && ((pkgDiff.added?.length ?? 0) > 0 || (pkgDiff.removed?.length ?? 0) > 0)) {
    const added = pkgDiff.added?.length ?? 0;
    const removed = pkgDiff.removed?.length ?? 0;
    files.push({
      name: 'Package Registry',
      description: `${added ? `${added} added` : ''}${added && removed ? ', ' : ''}${removed ? `${removed} removed` : ''}`,
      hasChanges: true,
      diffKey: 'packages',
    });
  }

  // AC-5: Approve & Continue
  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await api.syncApprove(runId, true);
      onApproved();
    } catch {
      setSubmitting(false);
    }
  };

  // AC-6: Skip for Now — approve but skip doc commit phases
  const handleSkip = async () => {
    setSubmitting(true);
    try {
      await api.syncApprove(runId, true, ['agents_md', 'worldview_generation', 'routing_rules']);
      onSkipped();
    } catch {
      setSubmitting(false);
    }
  };

  // AC-2: Prevent Escape key dismissal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, []);

  return (
    // AC-1: Blocking modal overlay — AC-2: no click-outside dismiss
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backdropFilter: 'blur(4px)',
      animation: 'fadeIn 0.3s ease',
    }}>
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        width: '100%', maxWidth: 560,
        maxHeight: '85vh', overflow: 'auto',
        padding: 28,
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 'var(--radius-md)',
            background: 'var(--orange-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <FileText size={18} style={{ color: 'var(--orange)' }} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--text)' }}>
              OCNexus wants to add files to your repo
            </h3>
            <p style={{ margin: '6px 0 0', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 1.5 }}>
              OCNexus analyzed your codebase and prepared documentation files that help it understand your project.
              These files teach OCNexus your coding style, tech stack, and project structure so it can generate better code for you.
            </p>
          </div>
        </div>

        {/* AC-3: Dynamic file list */}
        {files.length > 0 && (
          <div style={{
            background: 'var(--bg)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)', padding: 12, marginBottom: 16,
          }}>
            {files.map((file, i) => (
              <div key={file.diffKey} style={{
                padding: '10px 0',
                borderBottom: i < files.length - 1 ? '1px solid var(--border)' : 'none',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FileText size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                  <span style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--text)' }}>
                    {file.name}
                  </span>
                </div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginTop: 3, paddingLeft: 22 }}>
                  {file.description}
                </div>
              </div>
            ))}
          </div>
        )}

        {files.length === 0 && (
          <div style={{
            background: 'var(--bg)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)', padding: 16, marginBottom: 16,
            textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--text-sm)',
          }}>
            No file changes detected — OCNexus will use the analysis internally.
          </div>
        )}

        {/* Trust note */}
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)', marginBottom: 20, lineHeight: 1.5 }}>
          These will be committed to a new branch and opened as a pull request.
          Nothing merges to main without your approval in GitHub.
        </p>

        {/* AC-4: Preview Changes */}
        {files.length > 0 && !previewOpen && (
          <button
            className="btn btn-sm"
            onClick={() => setPreviewOpen(true)}
            style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <Eye size={12} /> Preview Changes
          </button>
        )}

        {previewOpen && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Change Preview
              </span>
              <button className="btn btn-sm" onClick={() => setPreviewOpen(false)} style={{ padding: '2px 6px' }}>
                <X size={10} />
              </button>
            </div>

            {/* AGENTS.md diff */}
            {agentsDiff && (agentsDiff.total_changes ?? 0) > 0 && (
              <div style={{ marginBottom: 8 }}>
                <button
                  className="btn btn-sm"
                  onClick={() => setExpandedFile(expandedFile === 'agents' ? null : 'agents')}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', justifyContent: 'flex-start', background: 'var(--bg)', border: '1px solid var(--border)' }}
                >
                  {expandedFile === 'agents' ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  <BookOpen size={12} />
                  <span style={{ fontSize: 'var(--text-xs)' }}>AGENTS.md — {agentsDiff.total_changes} changes</span>
                </button>
                {expandedFile === 'agents' && agentsDiff.changes && (
                  <div style={{ padding: '8px 12px', background: 'var(--bg)', borderRadius: 'var(--radius)', marginTop: 4, fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', maxHeight: 200, overflow: 'auto' }}>
                    {agentsDiff.changes.map((c, i) => (
                      <div key={i} style={{ color: c.type === 'added' ? 'var(--green)' : c.type === 'removed' ? 'var(--red)' : 'var(--yellow)', lineHeight: 1.8 }}>
                        {c.type === 'added' ? '+ Added: ' : c.type === 'removed' ? '- Removed: ' : '~ Updated: '}
                        {c.description}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Routing rules diff */}
            {routingDiff && (routingDiff.total_changes ?? 0) > 0 && (
              <div style={{ marginBottom: 8 }}>
                <button
                  className="btn btn-sm"
                  onClick={() => setExpandedFile(expandedFile === 'routing' ? null : 'routing')}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', justifyContent: 'flex-start', background: 'var(--bg)', border: '1px solid var(--border)' }}
                >
                  {expandedFile === 'routing' ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  <Route size={12} />
                  <span style={{ fontSize: 'var(--text-xs)' }}>Routing Rules — {routingDiff.total_changes} changes</span>
                </button>
              </div>
            )}

            {/* Packages diff */}
            {pkgDiff && ((pkgDiff.added?.length ?? 0) > 0 || (pkgDiff.removed?.length ?? 0) > 0) && (
              <div style={{ marginBottom: 8 }}>
                <button
                  className="btn btn-sm"
                  onClick={() => setExpandedFile(expandedFile === 'packages' ? null : 'packages')}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', justifyContent: 'flex-start', background: 'var(--bg)', border: '1px solid var(--border)' }}
                >
                  {expandedFile === 'packages' ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  <Database size={12} />
                  <span style={{ fontSize: 'var(--text-xs)' }}>Packages — {(pkgDiff.added?.length ?? 0) + (pkgDiff.removed?.length ?? 0)} changes</span>
                </button>
                {expandedFile === 'packages' && (
                  <div style={{ padding: '8px 12px', background: 'var(--bg)', borderRadius: 'var(--radius)', marginTop: 4, fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', maxHeight: 200, overflow: 'auto' }}>
                    {pkgDiff.added?.map((p, i) => (
                      <div key={`a-${i}`} style={{ color: 'var(--green)', lineHeight: 1.8 }}>+ {p} (new)</div>
                    ))}
                    {pkgDiff.removed?.map((p, i) => (
                      <div key={`r-${i}`} style={{ color: 'var(--red)', lineHeight: 1.8 }}>- {p} (removed)</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            className="btn btn-sm"
            onClick={handleSkip}
            disabled={submitting}
            style={{ color: 'var(--text-muted)' }}
          >
            <SkipForward size={12} /> Skip for Now
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleApprove}
            disabled={submitting}
            style={{ paddingLeft: 16, paddingRight: 16 }}
          >
            {submitting ? <Loader2 size={12} className="spin" /> : <Check size={12} />}
            {' '}Approve & Continue
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Task 4: Visual Phase States ───────────────────────────────

const PhaseIcon: React.FC<{ status: string; size?: number }> = ({ status, size = 14 }) => {
  switch (status) {
    case 'completed':
      return <CheckCircle size={size} style={{ color: 'var(--green)' }} />;
    case 'running':
      return <Loader2 size={size} className="spin" style={{ color: 'var(--orange)' }} />;
    case 'skipped':
      return <SkipForward size={size} style={{ color: 'var(--text-dim)' }} />;
    case 'failed':
      return <XCircle size={size} style={{ color: 'var(--red)' }} />;
    case 'review':
    case 'review_required':
      return <Pause size={size} style={{ color: 'var(--orange)' }} />;
    default: // pending / queued
      return <Circle size={size} style={{ color: 'var(--text-dim)' }} />;
  }
};

// ─── Task 3 + 4 + 6: Phase Progress List ──────────────────────

// VettIQ full-repository scan stages, in pipeline order. Mirrors
// REQUIRED_FULL_SCAN_STAGES in the API's vettiq-full-security-phase, which is
// the contract that guarantees all seven are present on a completed scan.
const VETTIQ_SCAN_STEPS = [
  ['clone', 'Clone & file collection'],
  ['dependency_scan', 'Dependency scan'],
  ['secret_scan', 'Secret scan'],
  ['sast', 'Static analysis (semgrep)'],
  ['iac_scan', 'IaC scan'],
  ['license_scan', 'License scan'],
  ['deep_analysis', 'Deep LLM analysis'],
] as const;

const PhaseList: React.FC<{ phases: SyncPhase[]; compact?: boolean }> = ({ phases, compact }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 4 : 6 }}>
    {phases.map((phase, idx) => {
      const display = getPhaseDisplay(phase.id);
      const isRunning = phase.status === 'running';
      const isCompleted = phase.status === 'completed';
      const isSkipped = phase.status === 'skipped';
      const isFailed = phase.status === 'failed';
      const isPending = phase.status === 'pending';
      const reviewSteps = phase.id === 'manus_audit'
        && phase.output?.review_steps
        && typeof phase.output.review_steps === 'object'
        && !Array.isArray(phase.output.review_steps)
        ? phase.output.review_steps as Record<string, unknown>
        : undefined;
      const scanSteps = phase.id === 'security_scan'
        && phase.output?.scan_steps
        && typeof phase.output.scan_steps === 'object'
        && !Array.isArray(phase.output.scan_steps)
        ? phase.output.scan_steps as Record<string, unknown>
        : undefined;

      // Review gate divider before agents_md
      const showReviewDivider = phase.id === 'agents_md' && idx > 0;

      return (
        <React.Fragment key={phase.id}>
          {showReviewDivider && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0',
              fontSize: 'var(--text-xs)', color: 'var(--text-dim)',
            }}>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              <span>review gate</span>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            </div>
          )}
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 10, padding: '4px 0',
            opacity: isPending ? 0.5 : 1,
            transition: 'opacity 0.3s ease',
          }}>
            {/* AC-15: Distinct visual state icon */}
            <div style={{ paddingTop: 1, flexShrink: 0 }}>
              <PhaseIcon status={phase.status} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {/* AC-8: User-facing name */}
                <span style={{
                  fontSize: 'var(--text-sm)', fontWeight: 500,
                  color: isPending ? 'var(--text-dim)' : isSkipped ? 'var(--text-dim)' : 'var(--text)',
                  textDecoration: isSkipped ? 'line-through' : 'none',
                }}>
                  {display.name}
                </span>
                {/* AC-16: Running animation label */}
                {isRunning && (
                  <span className="mono" style={{ fontSize: 'var(--text-xs)', color: 'var(--orange)', animation: 'pulse 2s infinite' }}>
                    running...
                  </span>
                )}
                {/* AC-28: Completed phases show actual duration */}
                {isCompleted && phase.duration_ms && (
                  <span className="mono" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)' }}>
                    {formatDurationShort(phase.duration_ms)}
                  </span>
                )}
                {/* Running phase elapsed time */}
                {isRunning && phase.started_at && (
                  <PhaseElapsed startedAt={phase.started_at} />
                )}
              </div>
              {/* AC-9: User-facing description (only show for non-compact or active states) */}
              {!compact && !isPending && display.description ? (
                <div className="muted" style={{ fontSize: 'var(--text-xs)', marginTop: 1 }}>{display.description}</div>
              ) : null}
              {/* AC-17: Skipped phases show reason */}
              {isSkipped ? (
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)', marginTop: 2, fontStyle: 'italic' }}>
                  {phase.error ? String(phase.error) : 'Skipped'}
                </div>
              ) : null}
              {/* Gemini pre-computation indicator on manus_audit phase */}
              {phase.id === 'manus_audit' && isPending && typeof phase.output?.gemini_status === 'string' ? (
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--blue, #60a5fa)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                  {phase.output.gemini_status === 'ingesting' ? (
                    <><Loader2 size={10} style={{ animation: 'spin 1s linear infinite' }} /> Pre-analyzing codebase with Gemini...</>
                  ) : phase.output.gemini_status === 'complete' ? (
                    <><CheckCircle size={10} /> {String(phase.output.summary ?? '')}</>
                  ) : phase.output.gemini_status === 'failed' ? (
                    <span style={{ color: 'var(--text-dim)' }}>Gemini pre-analysis unavailable</span>
                  ) : null}
                </div>
              ) : null}
              {/* Concurrent architecture-review seats and higher-level synthesis. */}
              {phase.id === 'manus_audit' && reviewSteps && (isRunning || isFailed) ? (
                <div style={{ fontSize: 'var(--text-xs)', marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {([['architecture', 'Architecture reviewer'], ['engineering', 'Engineering reviewer'], ['synthesis', 'Senior synthesis']] as const).map(([key, label]) => {
                    const raw = reviewSteps[key];
                    const step = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw as Record<string, unknown> : undefined;
                    const status = typeof step?.status === 'string' ? step.status : key === 'synthesis' ? 'waiting' : 'queued';
                    const detail = typeof step?.detail === 'string' ? step.detail : status === 'waiting' ? 'waiting for both reviewers' : status === 'queued' ? 'queued' : undefined;
                    return (
                      <div key={key} style={{ color: status === 'failed' ? 'var(--red)' : 'var(--blue, #60a5fa)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <PhaseIcon status={status === 'started' ? 'running' : status} size={10} />
                        <span>{label}</span>
                        <span style={{ color: 'var(--text-dim)' }}>— {detail ?? status}</span>
                      </div>
                    );
                  })}
                  {isFailed ? <div style={{ color: 'var(--orange)', marginTop: 2 }}>Review recovery: run a new sync or a manual deep review to retry.</div> : null}
                </div>
              ) : phase.id === 'manus_audit' && typeof phase.output?.review_stage === 'string' && (isRunning || isFailed) ? (
                <div style={{ fontSize: 'var(--text-xs)', color: isFailed ? 'var(--orange)' : 'var(--blue, #60a5fa)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                  {isRunning ? <Loader2 size={10} style={{ animation: 'spin 1s linear infinite' }} /> : <XCircle size={10} />}
                  <span>{String(phase.output.review_stage) === '2A' ? 'Architecture reviewer' : String(phase.output.review_stage) === '2B' ? 'Engineering reviewer' : String(phase.output.review_stage) === '3' ? 'Senior synthesis' : 'Review recovery'}: {String(phase.output.review_detail ?? (isFailed ? 'Review can be retried from a new sync.' : 'working...'))}</span>
                </div>
              ) : null}
              {/* VettIQ full-repository scan stages, streamed while the scan runs. */}
              {phase.id === 'security_scan' && scanSteps && (isRunning || isCompleted || isFailed) ? (
                <div style={{ fontSize: 'var(--text-xs)', marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {VETTIQ_SCAN_STEPS.map(([key, label]) => {
                    const raw = scanSteps[key];
                    const step = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw as Record<string, unknown> : undefined;
                    const status = typeof step?.status === 'string' ? step.status : 'pending';
                    const detail = typeof step?.detail === 'string' ? step.detail : undefined;
                    return (
                      <div key={key} style={{ color: status === 'failed' ? 'var(--red)' : 'var(--blue, #60a5fa)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <PhaseIcon status={status} size={10} />
                        <span>{label}</span>
                        {detail ? <span style={{ color: 'var(--text-dim)' }}>— {detail}</span> : null}
                      </div>
                    );
                  })}
                </div>
              ) : null}
              {/* AC-18: Failed phases show error */}
              {isFailed && phase.error && (
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--red)', marginTop: 2, fontFamily: 'var(--font-mono)' }}>
                  {String(phase.error)}
                </div>
              )}
            </div>
          </div>
        </React.Fragment>
      );
    })}
  </div>
);

// Elapsed time counter for running phases
const PhaseElapsed: React.FC<{ startedAt: string }> = ({ startedAt }) => {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = new Date(startedAt).getTime();
    const tick = () => setElapsed(Date.now() - start);
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  return (
    <span className="mono" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)' }}>
      {formatDurationShort(elapsed)} elapsed
    </span>
  );
};

// ─── Task 3: Progress Narrative ────────────────────────────────

function getProgressNarrative(phases: SyncPhase[], projectName: string, status: string): { title: string; subtitle: string } {
  if (status === 'completed') {
    return {
      title: `OCNexus is ready for ${projectName}`,
      subtitle: '',
    };
  }

  if (status === 'failed') {
    return {
      title: `Sync failed for ${projectName}`,
      subtitle: 'Check the error details below and retry when ready.',
    };
  }

  // Find the current running phase
  const runningPhase = phases.find(p => p.status === 'running');
  const completedPhases = phases.filter(p => p.status === 'completed');

  // Extract metadata from completed phases
  const repoScan = phases.find(p => p.id === 'repo_scan');
  const pkgDetect = phases.find(p => p.id === 'package_detection');

  // AC-13: File count after repo scan
  const fileCount = repoScan?.status === 'completed' && repoScan.output?.files_scanned
    ? repoScan.output.files_scanned : null;

  // AC-14: Tech stack after package detection
  const techStack = pkgDetect?.status === 'completed' && pkgDetect.output?.packages
    ? (pkgDetect.output.packages as Array<{ name?: string; type?: string; framework?: string }>)
        .slice(0, 5)
        .map(p => p.name || p.framework || p.type || 'unknown')
        .join(', ')
    : null;

  // Build subtitle
  let subtitle = '';
  if (fileCount) {
    subtitle += `Scanned ${fileCount} files`;
    if (techStack) subtitle += ` (${techStack})`;
    subtitle += '. ';
  }

  if (runningPhase) {
    const display = getPhaseDisplay(runningPhase.id);
    subtitle += display.description + '...';
  } else if (status === 'review_required') {
    subtitle = 'Waiting for your review before continuing.';
  } else if (status === 'audit_pending') {
    subtitle = 'Architectural review ready — choose your review depth.';
  } else if (completedPhases.length === 0) {
    subtitle = 'OCNexus is learning about your project so it can generate better code, match your style, and track quality over time.';
  }

  // AC-11: Project name in header
  const title = completedPhases.length >= phases.length - 2
    ? `Almost done setting up ${projectName}`
    : `Setting up OCNexus for ${projectName}`;

  return { title, subtitle };
}

// ─── Task 6: Progress Bar ──────────────────────────────────────

const ProgressBar: React.FC<{ phases: SyncPhase[]; status: string; startedAt?: string; previousDurationsMs?: number[] }> = ({ phases, status, startedAt, previousDurationsMs = [] }) => {
  const completed = phases.filter(p => p.status === 'completed' || p.status === 'skipped').length;
  const total = phases.length;
  const pct = total > 0 ? (completed / total) * 100 : 0;
  const isReview = status === 'review_required';
  const elapsedMs = startedAt ? Math.max(0, Date.now() - new Date(startedAt).getTime()) : 0;
  const remaining = estimateRemaining(phases, elapsedMs, previousDurationsMs);

  return (
    <div style={{ marginBottom: 16 }}>
      {/* AC-24: Progress bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <div style={{
          flex: 1, height: 8, borderRadius: 4,
          background: 'var(--surface2)', overflow: 'hidden',
        }}>
          {/* AC-29: Smooth animation */}
          <div style={{
            height: '100%', borderRadius: 4,
            background: isReview ? 'var(--orange)' : 'var(--green)',
            width: `${pct}%`,
            transition: 'width 0.6s ease, background 0.3s ease',
            position: 'relative',
          }}>
            {/* Shimmer on leading edge */}
            {status === 'running' && (
              <div style={{
                position: 'absolute', right: 0, top: 0, bottom: 0, width: 20,
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2))',
                animation: 'pulse 1.5s infinite',
              }} />
            )}
          </div>
        </div>
        <span className="mono" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
          {completed} of {total}
        </span>
      </div>
      {/* AC-25: Time estimate */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Clock size={11} style={{ color: 'var(--text-dim)' }} />
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)' }}>
          {formatTimeEstimate(remaining, isReview)}
        </span>
      </div>
    </div>
  );
};

// ─── Manus deep-review CTA (ocn-S16-P0 sec-4) ──────────────────
// Sits inside the completion summary as a fallback when the audit
// modal was dismissed or no providers were configured. Click hits
// POST /api/manus/audit/:projectId/trigger (already idempotent on
// the server via the gate check + Manus task semantics).
const DeepReviewCta: React.FC<{ projectId: string }> = ({ projectId }) => {
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trigger = async () => {
    if (!projectId || pending || done) return;
    setPending(true);
    setError(null);
    try {
      await api.manusAuditTrigger(projectId);
      setDone(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg || 'Failed to trigger deep review');
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 16, padding: '12px 16px', borderColor: 'var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-dim)', marginBottom: 6 }}>
            Architectural Review
          </div>
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}>
            Deep review skipped. You can run a Manus deep review now.
          </div>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)', marginTop: 4 }}>
            ~5-15 credits · ~10-20 min · architecture, security, tech debt
          </div>
          {error && (
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--red)', marginTop: 6 }}>
              {error}
            </div>
          )}
        </div>
        <button
          className="btn btn-sm"
          onClick={trigger}
          disabled={pending || done}
          style={{ flexShrink: 0 }}
        >
          {pending ? 'Triggering…' : done ? 'Triggered' : 'Run Manus deep review'}
        </button>
      </div>
    </div>
  );
};

// ─── Task 5: Sync Completion Summary ───────────────────────────

interface CompletionSummaryProps {
  phases: SyncPhase[];
  projectId: string;
  projectName: string;
  runId?: string;
  onGoToChat: () => void;
  onGoToProject: () => void;
  onGoToFindings: () => void;
}

const CompletionSummary: React.FC<CompletionSummaryProps> = ({
  phases, projectId, projectName, runId, onGoToChat, onGoToProject, onGoToFindings,
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [reviewComparisons, setReviewComparisons] = useState<Array<{ provider: string; status: string; findings_json: unknown; architecture_summary: string | null; tech_debt_score: number | null; tokens_used: number | null; cost_usd: number | null; duration_ms: number | null; p0_count: number | null; p1_count: number | null; p2_count: number | null }>>([]);

  // Fetch review comparison data if an audit phase completed
  useEffect(() => {
    if (!runId) return;
    const auditPhase = phases.find(p => p.id === 'manus_audit');
    if (auditPhase?.status === 'completed') {
      api.syncReviewComparison(runId).then(setReviewComparisons).catch(() => {});
    }
  }, [runId, phases]);

  // Extract data from phase outputs
  const pkgPhase = phases.find(p => p.id === 'package_detection');
  const guardianPhase = phases.find(p => p.id === 'guardian_baseline');
  const docPhase = phases.find(p => p.id === 'doc_audit');
  const securityPhase = phases.find(p => p.id === 'security_scan');
  const qualityPhase = phases.find(p => p.id === 'quality_checks');
  const sastPhase = phases.find(p => p.id === 'sast_scan');

  // Bug 6 fix: packages are objects {name, path, type, framework}, not strings.
  // Build a readable tech stack from unique frameworks and types.
  const techStack = (() => {
    const pkgs = pkgPhase?.output?.packages;
    if (!pkgs || !Array.isArray(pkgs) || pkgs.length === 0) return 'Not detected';
    const labels = new Set<string>();
    for (const pkg of pkgs) {
      if (typeof pkg === 'string') { labels.add(pkg); continue; }
      if (pkg.framework) labels.add(String(pkg.framework));
      if (pkg.type && pkg.type !== 'unknown') labels.add(String(pkg.type));
      if (labels.size === 0 && pkg.name) labels.add(String(pkg.name));
    }
    return labels.size > 0 ? Array.from(labels).join(' · ') : `${pkgs.length} package${pkgs.length > 1 ? 's' : ''} detected`;
  })();

  const guardianSummary = guardianPhase?.output?.summary || null;
  // Bug 1 fix: account for docs written by the sync itself (agents_md phase).
  // The doc_audit runs BEFORE the review gate so it sees the pre-sync state.
  // After approval, agents_md writes AGENTS.md directly to main. The completion
  // summary must reflect the POST-sync truth, not the pre-sync audit snapshot.
  const agentsMdPhase = phases.find(p => p.id === 'agents_md');
  const agentsMdCreated = agentsMdPhase?.output?.agents_md_updated === true;

  const docSummary = (() => {
    const audit = docPhase?.output?.doc_framework_audit as {
      contextMd?: { exists: boolean };
      agentsMd?: { exists: boolean };
      specMd?: { exists: boolean };
    } | undefined;
    if (audit) {
      const docs = [
        { name: 'CONTEXT.md', exists: audit.contextMd?.exists ?? false, note: '' },
        // Bug 1: if agents_md phase created AGENTS.md during this sync, show as ✅
        { name: 'AGENTS.md', exists: (audit.agentsMd?.exists ?? false) || agentsMdCreated, note: agentsMdCreated && !(audit.agentsMd?.exists ?? false) ? ' (added in sync)' : '' },
        { name: 'SPEC.md', exists: audit.specMd?.exists ?? false, note: '' },
      ];
      const found = docs.filter(d => d.exists).length;
      const missing = docs.filter(d => !d.exists).map(d => d.name);
      // Canary 2026-10-02: no red X's -- missing repo docs are not a failure; OCNexus
      // keeps its own project knowledge from this sync either way.
      return missing.length === 0
        ? 'All 3 project docs are in your repo (CONTEXT.md, AGENTS.md, SPEC.md).'
        : `${found} of 3 project docs are in your repo (missing ${missing.join(', ')}). OCNexus keeps its own project knowledge from this sync, so nothing is blocked.`;
    }
    // Fallback to API summary
    const raw = docPhase?.output?.summary;
    if (!raw) return null;
    const str = String(raw);
    // Strip meaningless "Framework: partial (0%)" pattern
    return str.replace(/\s*Framework:\s*\w+\s*\(\d+%\)/, '');
  })();

  // Fetch live findings summary (unresolved only) instead of cached phase output
  const [liveFindingsCount, setLiveFindingsCount] = useState(0);
  useEffect(() => {
    if (!projectId) return;
    api.findings.summary(projectId)
      .then((s) => setLiveFindingsCount(s.unresolved ?? 0))
      .catch(() => {});
  }, [projectId]);
  const findingsCount = liveFindingsCount;

  // SAST findings counts
  const sastTotal = Number(sastPhase?.output?.total_findings ?? 0);
  const sastP0 = Number(sastPhase?.output?.p0_count ?? 0);
  const sastP1 = Number(sastPhase?.output?.p1_count ?? 0);
  const sastP2 = Number(sastPhase?.output?.p2_count ?? 0);

  // Skipped phases for explanation
  const skippedPhases = phases.filter(p => p.status === 'skipped');
  const failedPhases = phases.filter(p => p.status === 'failed');

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <CheckCircle size={22} style={{ color: 'var(--green)' }} />
        <div>
          <h3 style={{ margin: 0, fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--text)' }}>
            OCNexus is ready for {projectName}
          </h3>
          <p style={{ margin: '2px 0 0', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
            Here's what we learned about your project:
          </p>
        </div>
      </div>

      {/* AC-19: Tech stack, quality baseline, doc status */}
      <div style={{ display: 'grid', gap: 14, marginBottom: 20 }}>
        {/* Tech Stack */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <Database size={14} style={{ color: 'var(--text-muted)', marginTop: 2, flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
              Tech Stack
            </div>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}>{techStack}</div>
          </div>
        </div>

        {/* Quality Baseline */}
        {guardianSummary && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <Shield size={14} style={{ color: 'var(--text-muted)', marginTop: 2, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                Code Quality Baseline
              </div>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}>{String(guardianSummary)}</div>
            </div>
          </div>
        )}

        {/* Documentation */}
        {docSummary && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <BookOpen size={14} style={{ color: 'var(--text-muted)', marginTop: 2, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                Documentation
              </div>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}>{String(docSummary)}</div>
            </div>
          </div>
        )}

        {/* Security */}
        {securityPhase && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <Lock size={14} style={{ color: 'var(--text-muted)', marginTop: 2, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                Security
              </div>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}>
                {securityPhase.status === 'completed'
                  ? String(securityPhase.output?.summary || 'Scan complete')
                  : securityPhase.status === 'skipped'
                    ? (/no material change/i.test(String(securityPhase.output?.summary ?? securityPhase.error ?? '')) ? 'Up to date — scanned recently, no code changes since.' : 'Skipped this time — it runs again on the next sync.')
                    : securityPhase.status === 'failed' || securityPhase.error
                      ? 'Security scan unavailable — this is optional and does not affect OCNexus functionality.'
                      : 'Not available'}
              </div>
            </div>
          </div>
        )}

        {/* SAST Scan */}
        {sastPhase && sastPhase.status === 'completed' && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <ShieldCheck size={14} style={{ color: sastP0 > 0 ? '#ef4444' : 'var(--text-muted)', marginTop: 2, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                SAST Security Scan
              </div>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}>
                {sastTotal === 0
                  ? 'No security issues found'
                  : String(sastPhase.output?.summary || `${sastTotal} finding${sastTotal === 1 ? '' : 's'}`)
                }
              </div>
              {sastTotal > 0 && (
                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                  {sastP0 > 0 && (
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}>
                      {sastP0} P0
                    </span>
                  )}
                  {sastP1 > 0 && (
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: 'rgba(249,115,22,0.15)', color: '#00E5FF' }}>
                      {sastP1} P1
                    </span>
                  )}
                  {sastP2 > 0 && (
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: 'rgba(234,179,8,0.15)', color: '#eab308' }}>
                      {sastP2} P2
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* AC-20: Findings count */}
      {(findingsCount > 0 || skippedPhases.length > 0 || failedPhases.length > 0) && (
        <div style={{
          background: 'var(--bg)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)', padding: 12, marginBottom: 16,
        }}>
          {findingsCount > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: skippedPhases.length > 0 ? 8 : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertTriangle size={12} style={{ color: 'var(--orange)' }} />
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}>
                  {findingsCount} finding{findingsCount > 1 ? 's' : ''} need attention
                </span>
              </div>
              <button className="btn btn-sm" onClick={onGoToFindings} style={{ fontSize: 'var(--text-xs)', padding: '2px 8px' }}>
                View Findings <ArrowRight size={10} />
              </button>
            </div>
          )}

          {/* AC-22: Skipped phases explained */}
          {skippedPhases.map(p => (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 'var(--text-xs)', color: 'var(--text-dim)' }}>
              <SkipForward size={10} />
              <span>{getPhaseDisplay(p.id).name} — {p.error || 'skipped'}</span>
            </div>
          ))}
        </div>
      )}

      {/* Architecture review results */}
      {reviewComparisons.length > 0 ? (
        <div style={{ marginBottom: 16 }}>
          <ReviewComparison comparisons={reviewComparisons} />
        </div>
      ) : (() => {
        const auditPhase = phases.find(p => p.id === 'manus_audit');
        if (!auditPhase || auditPhase.status === 'skipped') return null;
        if (auditPhase.status !== 'completed' || !auditPhase.output) return null;
        const out = auditPhase.output as Record<string, unknown>;

        // Bug 2 fix: detect empty-review signature (review_error flag set by backend,
        // or zero cost + zero duration + zero findings = provider never ran).
        // Show a distinct retryable error instead of misleading "0 findings, $0.000".
        const isReviewError = out.review_error === true;
        if (isReviewError) {
          return (
            <div className="card" style={{ marginBottom: 16, padding: '12px 16px', borderColor: 'var(--orange)', borderWidth: 1, borderStyle: 'solid' }}>
              <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--orange)', marginBottom: 6 }}>
                Architectural Review — Did Not Complete
              </div>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}>
                Deep review encountered an issue ({String(out.review_error_detail || 'empty result')}) and did not produce findings.
                This is a provider issue, not a problem in your code.
              </div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)', marginTop: 6 }}>
                Run a new sync or trigger a manual deep review to retry.
              </div>
            </div>
          );
        }

        return (
          <div className="card" style={{ marginBottom: 16, padding: '12px 16px' }}>
            <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-dim)', marginBottom: 6 }}>
              Architectural Review
            </div>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
              {String(out.summary || 'Review completed')}
            </div>
            {out.findings_count != null && (
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)', marginTop: 4 }}>
                {String(out.findings_count)} findings · Tech debt score: {String(out.tech_debt_score ?? '?')}/100
                {out.cost_usd != null && <span> · ${Number(out.cost_usd).toFixed(3)}</span>}
              </div>
            )}
          </div>
        );
      })()}

      {/* Manus deep-review CTA (ocn-S16-P0 sec-4) ──────────────
          Complementary fallback to the polling-based audit modal.
          Shows when the manus_audit phase ended in 'skipped' (operator
          dismissed the modal, or no providers were configured) so the
          operator can still trigger a deep review on demand. Hidden
          when a review already ran or is in flight. */}
      {(() => {
        const auditPhase = phases.find(p => p.id === 'manus_audit');
        if (!auditPhase) return null;
        if (auditPhase.status !== 'skipped') return null;
        if (reviewComparisons.length > 0) return null;
        return <DeepReviewCta projectId={projectId} />;
      })()}

      {/* AC-21: What's next? */}
      <div style={{
        borderTop: '1px solid var(--border)', paddingTop: 16,
      }}>
        <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
          What's next?
        </div>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.5 }}>
          Go to Chat and describe a bug or feature request. OCNexus will classify it, write a spec, implement the code, run tests, and open a PR — automatically.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary btn-sm" onClick={onGoToChat} style={{ paddingLeft: 14, paddingRight: 14 }}>
            <MessageSquare size={12} /> Go to Chat
          </button>
          <button className="btn btn-sm" onClick={onGoToProject}>
            View Project Details
          </button>
        </div>
      </div>

      {/* Show phase details toggle */}
      <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
        <button
          className="btn btn-sm"
          onClick={() => setShowDetails(!showDetails)}
          style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)', display: 'flex', alignItems: 'center', gap: 4 }}
        >
          {showDetails ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
          {showDetails ? 'Hide' : 'Show'} phase details
        </button>
        {showDetails && (
          <div style={{ marginTop: 8 }}>
            <PhaseList phases={phases} compact />
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Sync History Row ──────────────────────────────────────────

const SyncHistoryRow: React.FC<{ run: SyncRun; expanded: boolean; onToggle: () => void }> = ({ run, expanded, onToggle }) => (
  <div>
    <div
      onClick={onToggle}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
        cursor: 'pointer', borderBottom: expanded ? 'none' : '1px solid var(--border)',
      }}
    >
      <span style={{
        width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
        background: statusColor(run.status),
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--text)' }}>
            {statusLabel(run.status)}
          </span>
          <span className="badge" style={{ fontSize: 10 }}>
            {run.triggered_by === 'auto_suggestion' ? 'auto' : 'manual'}
          </span>
        </div>
        <div className="mono muted" style={{ fontSize: 'var(--text-xs)', marginTop: 2 }}>
          {run.started_at ? timeAgo(run.started_at) : ''}
          {run.duration_ms ? ` · ${formatDuration(run.duration_ms)}` : ''}
        </div>
      </div>
      {expanded ? <ChevronDown size={12} className="muted" /> : <ChevronRight size={12} className="muted" />}
    </div>
    {expanded && run.phases && (
      <div style={{ padding: '8px 14px 14px', borderBottom: '1px solid var(--border)' }}>
        <PhaseList phases={run.phases} compact />
      </div>
    )}
  </div>
);

// ─── Main Sync Page ────────────────────────────────────────────

interface SyncPageProps {
  onBack: () => void;
  projectId?: string;
  /** Run the caller (e.g. the chat Sync panel) wants reviewed; a notice is shown when another run is current. */
  expectedRunId?: string;
  onNavigate?: (page: string) => void;
}

export const SyncPage: React.FC<SyncPageProps> = ({ onBack, projectId, expectedRunId, onNavigate }) => {
  const [history, setHistory] = useState<SyncRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedRun, setExpandedRun] = useState<string | null>(null);

  // Active sync state
  const [activeSyncId, setActiveSyncId] = useState<string | null>(null);
  const [activePhases, setActivePhases] = useState<SyncPhase[]>([]);
  const [activeStatus, setActiveStatus] = useState<string>('');
  const [projectName, setProjectName] = useState('your project');
  const [activeStartedAt, setActiveStartedAt] = useState<string | undefined>();
  const [previousDurationsMs, setPreviousDurationsMs] = useState<number[]>([]);
  const [reviewData, setReviewData] = useState<{ agents_md_diff: unknown; routing_rules_diff: unknown; packages_diff: unknown } | null>(null);
  const [sseDisconnected, setSseDisconnected] = useState(false);
  const sseRetryCountRef = useRef(0);
  const eventSourceRef = useRef<EventSource | null>(null);

  // AC-23: Track last completed sync for persistent summary
  const [lastCompletedRun, setLastCompletedRun] = useState<SyncRun | null>(null);
  const [syncEstimate, setSyncEstimate] = useState<{ minutes: number; label: string } | null>(null);
  const [codexEntryError, setCodexEntryError] = useState(false);
  const openingCodexSync = useRef<string | null>(null);
  const codexEntryScope = useRef(projectId); codexEntryScope.current = projectId;
  const codexEntryMounted = useRef(true);
  useEffect(() => { codexEntryMounted.current = true; return () => { codexEntryMounted.current = false; }; }, []);
  const openAfterSync = useCallback(async (syncId: string): Promise<void> => {
    if (!onNavigate || !projectId || openingCodexSync.current === syncId) return;
    const originalProject = projectId; openingCodexSync.current = syncId; setCodexEntryError(false);
    try {
      const destination = await openLiveSyncConversation(syncId);
      if (codexEntryMounted.current && codexEntryScope.current === originalProject) onNavigate(destination);
    } catch {
      if (codexEntryMounted.current && codexEntryScope.current === originalProject) setCodexEntryError(true);
    } finally { if (openingCodexSync.current === syncId) openingCodexSync.current = null; }
  }, [onNavigate, projectId]);

  const fetchHistory = useCallback(async () => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const runs = await api.syncHistory(projectId, 20);
      setHistory(runs);
      setPreviousDurationsMs(runs.filter(run => run.status === 'completed' && run.duration_ms && run.duration_ms > 0).map(run => run.duration_ms as number));
      // AC-23: If no active sync, show last completed run's summary
      if (!activeSyncId && runs.length > 0 && runs[0].status === 'completed') {
        setLastCompletedRun(runs[0]);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [projectId, activeSyncId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Check for active sync on mount
  useEffect(() => {
    if (!projectId) return;
    api.getProject(projectId).then((project) => {
      const value = project as unknown as Record<string, unknown>;
      const repo = value.repo as Record<string, unknown> | undefined;
      const name = value.name || value.display_name || repo?.name || value.repo_name;
      if (typeof name === 'string' && name.trim()) setProjectName(name.trim());
    }).catch(() => {});
    api.syncStatus(projectId).then((res) => {
      if (res.last_run && (res.last_run.status === 'running' || res.last_run.status === 'review_required' || res.last_run.status === 'audit_pending')) {
        setActiveSyncId(res.last_run.id);
        setActivePhases(res.last_run.phases || []);
        setActiveStartedAt(res.last_run.started_at);
        setActiveStatus(res.last_run.status);
        setLastCompletedRun(null); // hide summary when active sync exists
        if (res.last_run.status === 'review_required') {
          setReviewData({
            agents_md_diff: res.last_run.agents_md_diff,
            routing_rules_diff: res.last_run.routing_rules_diff,
            packages_diff: res.last_run.packages_diff,
          });
        } else if (res.stream_url) {
          connectSSE(res.last_run.id, (res as { stream_token?: string }).stream_token ?? null);
        }
      }
    }).catch(() => {});
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const connectSSE = useCallback(async (runId: string, streamToken?: string | null) => {
    eventSourceRef.current?.close();
    let token = streamToken ?? null;
    if (!token) {
      const accessToken = getCachedSession()?.access_token;
      if (accessToken) token = await fetchStreamToken('sync_run', runId, accessToken);
    }
    const url = buildProtectedStreamUrl(API_BASE, `/api/sync/stream/${runId}`, token);
    if (!url || !projectId) { setSseDisconnected(true); startPollingFallback(runId); return; }
    const handle = openSyncRunStream({
      url,
      fetchStatus: () => api.syncStatus(projectId),
      statusOf: (value) => value.last_run?.status,
      onEvent: (event) => {
        try {
          const data = JSON.parse(event.data);
          setSseDisconnected(false);
          if (data.phases) setActivePhases(data.phases);
          if (data.status) setActiveStatus(data.status);
          if (data.status === 'review_required') setReviewData({ agents_md_diff: data.agents_md_diff, routing_rules_diff: data.routing_rules_diff, packages_diff: data.packages_diff });
        } catch { /* ignore malformed stream events */ }
      },
      onStatus: (value) => {
        const run = value.last_run;
        if (!run) return;
        setActivePhases(run.phases || []);
        setActiveStatus(run.status);
      },
      onTerminal: (status) => {
        setActiveSyncId(null);
        setReviewData(null);
        setSyncEstimate(null);
        if (status === 'completed') {
          api.syncStatus(projectId).then(res => { if (res.last_run) setLastCompletedRun(res.last_run as SyncRun); }).catch(() => {});
          void openAfterSync(runId);
        }
        fetchHistory();
      },
    });
    eventSourceRef.current = { close: handle.close } as EventSource;
  }, [fetchHistory, projectId, openAfterSync]);

  // Polling fallback ref so we can clean it up
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startPollingFallback = useCallback((runId: string) => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    pollIntervalRef.current = setInterval(async () => {
      try {
        if (!projectId) return;
        const res = await api.syncStatus(projectId);
        if (!res.last_run) return;
        const run = res.last_run;
        if (run.phases) setActivePhases(run.phases);
        if (run.status) setActiveStatus(run.status);

        if (run.status === 'review_required') {
          setReviewData({
            agents_md_diff: run.agents_md_diff,
            routing_rules_diff: run.routing_rules_diff,
            packages_diff: run.packages_diff,
          });
        }

        if (['completed', 'failed', 'cancelled'].includes(run.status)) {
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          if (run.status === 'completed' && run.phases) {
            setLastCompletedRun(run as SyncRun);
            if (run.id === runId) void openAfterSync(runId);
          }
          setActiveSyncId(null);
          setReviewData(null);
          setSseDisconnected(false);
          fetchHistory();
        }
      } catch { /* ignore polling errors */ }
    }, 3000);
  }, [projectId, fetchHistory, openAfterSync]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) eventSourceRef.current.close();
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  // ── Gate-state polling (ocn-S16-P0) ─────────────────────────
  // Drives the audit-approval modal independently of the SSE stream.
  // SSE intermittently fails on Railway (ERR_HTTP2_PROTOCOL_ERROR), so
  // we poll /api/sync/:runId/gate-state every 4s while a sync is active.
  // Status updates flow through setActiveStatus, so the existing
  // ArchReviewModal renders as soon as gate-state == 'audit_pending'.
  useEffect(() => {
    if (!activeSyncId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const gate = await api.syncGateState(activeSyncId);
        if (cancelled) return;
        // Mirror raw status onto activeStatus so ArchReviewModal trigger fires.
        setActiveStatus((prev) => (prev === gate.status ? prev : gate.status));
        if (gate.state === 'review_complete' || gate.state === 'error' || gate.status === 'completed' || gate.status === 'failed' || gate.status === 'cancelled') {
          // Terminal — no need to keep polling. SSE/onerror handler will
          // also clear activeSyncId on completion; this just avoids extra
          // requests while that finalization is in flight.
          clearInterval(handle);
        }
      } catch { /* ignore — surface via SSE/error states */ }
    };
    const handle = setInterval(tick, 4000);
    void tick();
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [activeSyncId]);

  const startSync = async () => {
    if (!projectId || activeSyncId) return;
    try {
      setLastCompletedRun(null); // clear summary when starting new sync
      const res = await api.syncRun(projectId);
      setActiveSyncId(res.run_id);
      setActivePhases(res.phases);
      setActiveStartedAt(new Date().toISOString());
      setActiveStatus('running');
      setReviewData(null);
      if (res.estimate) setSyncEstimate({ minutes: res.estimate.minutes, label: res.estimate.label });
      connectSSE(res.run_id, (res as { stream_token?: string }).stream_token ?? null);
    } catch (err: unknown) {
      const errObj = err as { status?: number; run_id?: string; stream_token?: string };
      if (errObj.status === 409 && errObj.run_id) {
        setActiveSyncId(errObj.run_id);
        connectSSE(errObj.run_id, errObj.stream_token ?? null);
      }
    }
  };

  const handleApproved = () => {
    setReviewData(null);
    setActiveStatus('running');
    if (activeSyncId) connectSSE(activeSyncId);
  };

  const handleSkipped = () => {
    setReviewData(null);
    setActiveStatus('running');
    if (activeSyncId) connectSSE(activeSyncId);
  };

  const handleCancelled = () => {
    setActiveSyncId(null);
    setActivePhases([]);
    setActiveStatus('');
    setReviewData(null);
    setSyncEstimate(null);
    fetchHistory();
  };

  // Narrative
  const narrative = useMemo(() => {
    if (activeSyncId && activePhases.length > 0) {
      return getProgressNarrative(activePhases, projectName, activeStatus);
    }
    return null;
  }, [activeSyncId, activePhases, activeStatus, projectName]);

  // AC-26: Current phase name
  const currentPhase = useMemo(() => {
    const running = activePhases.find(p => p.status === 'running');
    return running ? getPhaseDisplay(running.id).name : null;
  }, [activePhases]);

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      {syncRunMismatch({ expectedRunId, loading, activeRunId: activeSyncId, latestRunId: history[0]?.id ?? null }) && (
        <div role="alert" className="card" style={{ padding: 12, marginBottom: 12 }}>
          The Sync you opened ({expectedRunId}) is not the current Sync for this project. What is shown below is the project&apos;s current Sync;
          the earlier run is listed under history if it is still retained.
        </div>
      )}
      {/* Header */}
      <div className="page-title-row" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-sm" onClick={onBack} style={{ padding: 6 }}>
            <ArrowLeft size={14} />
          </button>
          <div>
            <h2 className="page-title" style={{ marginBottom: 0 }}>Sync</h2>
            <p className="page-subtitle">
              {projectId ? `Project knowledge sync for ${projectName}` : 'Sync project knowledge'}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            className={`btn btn-sm ${!activeSyncId && projectId ? 'btn-primary' : ''}`}
            onClick={startSync}
            disabled={!!activeSyncId || !projectId}
          >
            {activeSyncId ? (
              <><Loader2 size={12} className="spin" /> Syncing...</>
            ) : (
              <><Wand2 size={12} /> Sync Project</>
            )}
          </button>
          <button className="btn btn-sm" onClick={fetchHistory}>
            <RefreshCw size={12} />
          </button>
        </div>
      </div>

      {/* SSE disconnect support prompt */}
      {sseDisconnected && (
        <div style={{
          padding: '10px 14px', marginBottom: 16, fontSize: 'var(--text-xs)',
          color: 'var(--red)', fontFamily: 'var(--font-mono)',
          background: 'var(--red-dim)', borderRadius: 'var(--radius-md)',
          border: '1px solid var(--red)', display: 'flex', alignItems: 'center',
          gap: 8, flexWrap: 'wrap',
        }}>
          <span>Connection lost — sync stream disconnected after multiple retries.</span>
          <button
            className="btn btn-sm"
            onClick={() => {
              setSseDisconnected(false);
              sseRetryCountRef.current = 0;
              if (activeSyncId) connectSSE(activeSyncId);
            }}
            style={{ fontSize: 'var(--text-xs)', padding: '2px 8px' }}
          >
            Retry
          </button>
          <a
            href="mailto:support@ocnexus.dev"
            style={{
              color: 'var(--red)', textDecoration: 'underline',
              fontSize: 'var(--text-xs)', fontFamily: 'inherit',
            }}
          >
            Contact support
          </a>
        </div>
      )}

      {/* ═══ AC-27: Active sync is primary page content ═══ */}
      {activeSyncId && (
        <div className="card" style={{ marginBottom: 16 }}>
          {/* Task 3: Progress narrative header */}
          {narrative && (
            <div style={{ marginBottom: 16 }}>
              {/* AC-11: Project name in title */}
              <h3 style={{ margin: 0, fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--text)' }}>
                {narrative.title}
              </h3>
              {/* AC-12: Dynamic subtitle */}
              {narrative.subtitle && (
                <p style={{ margin: '4px 0 0', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  {narrative.subtitle}
                </p>
              )}
            </div>
          )}

          {/* Task 6: Progress bar */}
          <ProgressBar phases={activePhases} status={activeStatus} startedAt={activeStartedAt} previousDurationsMs={previousDurationsMs} />

          {/* Sync estimate banner */}
          {syncEstimate && activeStatus === 'running' && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12,
              padding: '8px 12px', background: 'var(--bg)', borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-sm)', color: 'var(--text-muted)',
            }}>
              <span>Estimated time: <strong style={{ color: 'var(--text)' }}>{syncEstimate.label}</strong></span>
              <span style={{ opacity: 0.4 }}>|</span>
              <span>Estimated credits: <strong style={{ color: 'var(--text)' }}>~{syncEstimate.minutes <= 6 ? '50-100' : syncEstimate.minutes <= 10 ? '100-200' : '200-400'}</strong></span>
            </div>
          )}

          {/* AC-26: Current phase shown prominently */}
          {currentPhase && activeStatus === 'running' && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14,
              padding: '8px 12px', background: 'var(--bg)', borderRadius: 'var(--radius-md)',
            }}>
              <Loader2 size={13} className="spin" style={{ color: 'var(--orange)' }} />
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}>
                Currently: <strong>{currentPhase}</strong>
              </span>
            </div>
          )}

          {activeStatus === 'audit_pending' && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14,
              padding: '8px 12px', background: 'var(--orange-dim)', borderRadius: 'var(--radius-md)',
              border: '1px solid var(--orange)',
            }}>
              <Pause size={13} style={{ color: 'var(--orange)' }} />
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--orange)', fontWeight: 500 }}>
                Architectural review ready — choose your review depth
              </span>
            </div>
          )}

          {activeStatus === 'review_required' && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14,
              padding: '8px 12px', background: 'var(--orange-dim)', borderRadius: 'var(--radius-md)',
              border: '1px solid var(--orange)',
            }}>
              <Pause size={13} style={{ color: 'var(--orange)' }} />
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--orange)', fontWeight: 500 }}>
                Waiting for your review
              </span>
            </div>
          )}

          {/* Phase list */}
          <PhaseList phases={activePhases} />
        </div>
      )}

      {/* Task 1: Review gate modal (AC-1: blocking overlay) */}
      {activeSyncId && activeStatus === 'review_required' && reviewData && (
        <ReviewModal
          runId={activeSyncId}
          agentsMdDiff={reviewData.agents_md_diff}
          routingRulesDiff={reviewData.routing_rules_diff}
          packagesDiff={reviewData.packages_diff}
          onApproved={handleApproved}
          onSkipped={handleSkipped}
        />
      )}

      {/* Architectural review credit warning modal */}
      {activeSyncId && activeStatus === 'audit_pending' && projectId && (
        <ArchReviewModal
          runId={activeSyncId}
          projectId={projectId}
          onApprove={() => {
            setActiveStatus('running');
          }}
          onSkip={() => {
            setActiveSyncId(null);
            setActiveStatus('');
            api.syncStatus(projectId).then((res) => {
              if (res.last_run) setLastCompletedRun(res.last_run as any);
            }).catch(() => {});
          }}
        />
      )}

      {/* Post-sync next step: P0/P1 first, one click into the project chat (0d89d480) */}
      {codexEntryError && <p role="alert">Your Sync is complete. Codex could not open yet; your findings are preserved. Use Open chat to try again.</p>}
      {!activeSyncId && lastCompletedRun && projectId && <SyncNextStepCard projectId={projectId} syncId={lastCompletedRun.id} lastRunStatus={(lastCompletedRun as any).status} go={(p: string) => onNavigate?.(p)} />}

      {/* Task 5: Completion summary (AC-23: persists on return) */}
      {!activeSyncId && lastCompletedRun && lastCompletedRun.phases && (
        <CompletionSummary
          phases={lastCompletedRun.phases}
          projectId={projectId || ''}
          projectName={projectName}
          runId={lastCompletedRun.id}
          onGoToChat={() => {
            void openAfterSync(lastCompletedRun.id);
          }}
          onGoToProject={() => onNavigate?.(`project:${projectId}`)}
          onGoToFindings={() => {
            sessionStorage.setItem('project_target_tab', 'findings');
            onNavigate?.(`project:${projectId}`);
          }}
        />
      )}

      {/* Code Health trend (shows when debt metrics exist) */}
      {projectId && <CodeHealth projectId={projectId} />}

      {/* Sync history */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div className="section-label" style={{ marginBottom: 0 }}>Sync History</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {history.some((r: SyncRun) => r.status === 'running') && (
            <button
              className="btn btn-ghost btn-sm"
              style={{ fontSize: 'var(--text-xs)', opacity: 0.7 }}
              onClick={async () => {
                if (!projectId) return;
                try {
                  const hdrs = await authHeaders();
                  await fetch(`${API_BASE}/api/sync/cleanup-stale/${projectId}`, { method: 'POST', headers: hdrs });
                  fetchHistory();
                } catch { /* ignore */ }
              }}
            >
              Clear stale runs
            </button>
          )}
          {history.length > 1 && (
            <button
              className="btn btn-ghost btn-sm"
              style={{ fontSize: 'var(--text-xs)', opacity: 0.7 }}
              onClick={async () => {
                if (!projectId) return;
                try {
                  await api.clearSyncHistory(projectId, 1);
                  fetchHistory();
                } catch { /* ignore */ }
              }}
            >
              Clear History
            </button>
          )}
        </div>
      </div>
      {loading ? (
        <div className="empty-state"><div className="spinner" /><span className="muted">Loading history...</span></div>
      ) : history.length === 0 ? (
        <div className="empty-state" style={{ border: '1px dashed var(--border)', borderRadius: 'var(--radius-lg)' }}>
          <span className="muted" style={{ fontSize: 'var(--text-sm)' }}>
            No sync runs yet. Run a sync to build OCNexus's understanding of this codebase.
          </span>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          {history.map((run) => (
            <SyncHistoryRow
              key={run.id}
              run={run}
              expanded={expandedRun === run.id}
              onToggle={() => setExpandedRun(prev => prev === run.id ? null : run.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
